import { useEffect, useState } from "react";
import NavBar from "../../component/navBar/NavBar";
import { useAuth } from "../../config/utilities/useAuth";
import {
  APICreateDriver,
  APIDeleteDriver,
  APIGetDrivers,
  APIUpdateDriver,
} from "../../config/utilities/APIs/driversApi";
import { DriverForm } from "./DriverForm";
import Table from "../../component/table/Table";
import DeleteModal from "../../component/modal/DeleteModal";
import SnackBar from "../../component/Snackbar/Snackbar";
import Title from "../../component/title/Title";
import { removePlusPhone } from "../../config/utilities/formatPhone";
import DeactivationModal from "../../component/modal/DeactivationModal";
import "./Drivers.css";

const headers = [
  { key: "name", name: "Driver Name", sortable: false },
  { key: "type", name: "Driver Type", sortable: false },
  { key: "phoneNumber", name: "Phone Number", sortable: false },
  { key: "email", name: "Email", sortable: false },
  { key: "status", name: "Status", sortable: false },
];

const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

export default function Drivers() {
  const { privilege, token } = useAuth();
  const role = privilege?.role;
  const institutionId = privilege?.institutionId;
  const [action, setAction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [fetchedDriverData, setFetchedDriverData] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    status: "",
  });

  const filters = [
    { name: "status", placeholder: "Filter by Status", options: statusOptions },
  ];

  const [input, setInput] = useState({
    name: "",
    type: "",
    phoneNumber: "",
    email: "",
    profileImage: "",
    password: "",
    status: "active",
    preferredName: "",
  });

  const [error, setError] = useState({
    name: false,
    type: false,
    phoneNumber: false,
    email: false,
    profileImage: false,
    password: false,
    preferredName: false,
  });

  const fetchDrivers = async (
    page = 1,
    filters = selectedFilters,
    searchTerm,
  ) => {
    setLoading(true);

    try {
      const filterParams = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== ""),
      );
      const response = await APIGetDrivers(
        page,
        filterParams,
        setLoading,
        institutionId,
        searchTerm,
      );

      if (response.status === 200) {
        const { data } = response.data;
        setFetchedDriverData(data);
        setPageNumber(page);
      } else if (response.status === 400) {
        console.warn("Bad Request:", response);
      } else if (response.status === 401) {
        console.warn("Unauthorized:", response);
      } else if (response.status === 403) {
        console.warn("Forbidden:", response);
      } else if (response.status === 404) {
        console.warn("Not Found:", response);
      } else if (response.status >= 500) {
        console.error("Server Error:", response);
      } else {
        console.warn("Unexpected Status Code:", response.status, response);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    fetchDrivers(1, selectedFilters, searchTerm);
  }, [selectedFilters, searchTerm]);

  const handleReset = async () => {
    setInput({
      name: "",
      type: "",
      phoneNumber: "",
      email: "",
      profileImage: "",
      password: "",
      status: "active",
      preferredName: "",
    });
    setError({
      name: false,
      type: false,
      phoneNumber: false,
      email: false,
      profileImage: false,
      password: false,
      preferredName: false,
    });
    setFetchedDriverData([]);
    setShowDeleteModal(false);
    setShowDeactivationModal(false);
    setSelectedRowData(null);
    setAction(null);
    setPageNumber(1);
    await fetchDrivers(1, selectedFilters, searchTerm);
  };

  const handleDelete = async (row) => {
    setLoading(true);

    const rowId = row._id;

    try {
      const response = await APIDeleteDriver(
        row.email,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Delete Successful");
        setSnackbarSubtitle("Driver has been retired successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to retire driver",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Retire Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleEdit = async () => {
    setLoading(true);

    const formattedInput = formatInput(input);

    try {
      const response = await APIUpdateDriver(
        formattedInput,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Update Successful");
        setSnackbarSubtitle("Driver has been updated successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to update driver",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Update Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleCreate = async () => {
    setLoading(true);

    const formattedInput = formatInput(input);

    try {
      const response = await APICreateDriver(
        formattedInput,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Create Successful");
        setSnackbarSubtitle("Driver has been created successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to create driver",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Create Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleStatus = async (action) => {
    setLoading(true);

    const isActivate = action === "activate";

    const status = isActivate ? "active" : "inactive";
    const title = isActivate ? "Activation" : "Deactivation";

    const payload = {
      status,
      email: selectedRowData.email,
      firebaseUid: selectedRowData.firebaseUid,
    };

    try {
      const response = await APIUpdateDriver(
        payload,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle(`${title} Successful`);
        setSnackbarSubtitle(
          `Driver has been ${isActivate ? "activated" : "deactivated"} successfully`,
        );
        setSnackbarType("success");
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle(`${title} Failed`);
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handlePopoverAction = (action, rowId) => {
    const selectedRow = fetchedDriverData.drivers.find(
      (row) => row._id === rowId,
    );
    if (action === "edit") {
      setSelectedRowData(selectedRow);
      setAction("edit");
    } else if (action === "delete") {
      setSelectedRowData(selectedRow);
      setShowDeleteModal(true);
    } else if (action === "activate") {
      setSelectedRowData(selectedRow);
      setAction("activate");
      setShowDeactivationModal(true);
    } else if (action === "deactivate") {
      setSelectedRowData(selectedRow);
      setAction("deactivate");
      setShowDeactivationModal(true);
    } else {
      setSelectedRowData(selectedRow);
      setAction("view");
    }
  };

  const handleOpenCreate = () => {
    setAction("create");
  };

  const formatInput = (input) => {
    const { profileImage, ...rest } = input;

    return {
      ...rest,
      ...(profileImage?.startsWith("data:image") ? { profileImage } : {}),
      phoneNumber: removePlusPhone(input.phoneNumber || ""),
    };
  };

  const renderTitle = () => {
    switch (action) {
      case "edit":
        return (
          <Title
            title="Edit Driver"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "create":
        return (
          <Title
            title="Create Driver"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "view":
        <Title
          title="View Driver"
          titleSize="1.5rem"
          gap="0.5rem"
          lineHeight="2.5rem"
        />;
      default:
        return (
          <Title
            title="Driver Management"
            subtitle="Manage all the drivers in your institution"
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        );
    }
  };

  const renderContent = () => {
    switch (action) {
      case "edit":
        return (
          <DriverForm
            mode="edit"
            existingData={selectedRowData}
            onSubmit={handleEdit}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            role={role}
          />
        );
      case "create":
        return (
          <DriverForm
            mode="create"
            onSubmit={handleCreate}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
          />
        );
      case "view":
        return (
          <DriverForm
            mode="view"
            existingData={selectedRowData}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
          />
        );
      default:
        return (
          <Table
            data={fetchedDriverData?.drivers || []}
            searchText="Search by name, email, or phone number..."
            headers={headers}
            showEmptyHeader={true}
            showFilter={true}
            handlePopoverAction={handlePopoverAction}
            onClick={handleOpenCreate}
            module="drivers"
            loading={loading}
            showTableAction={
              role === "ADMIN" || role === "SUPERADMIN" || role === "EDITOR"
            }
            height="75vh"
            useBackendPagination={true}
            onPageChange={fetchDrivers}
            totalPages={fetchedDriverData?.pagination?.totalPages || 1}
            totalRecords={fetchedDriverData?.pagination?.total || 0}
            currentPage={pageNumber}
            filters={filters}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        );
    }
  };

  return (
    <div className="main-page drivers-page">
      <NavBar type="drivers" action={action} handleReset={handleReset} />
      <div className="content-area drivers-content-area">
        <div className="title-container mobile-hide-page-title">{renderTitle()}</div>
        <div className="main-content-container drivers-main-content-container">{renderContent()}</div>
      </div>
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        itemName={selectedRowData}
        module="drivers"
        loading={loading}
      />
      <DeactivationModal
        isOpen={showDeactivationModal}
        onClose={() => setShowDeactivationModal(false)}
        loading={loading}
        onToggle={handleStatus}
        itemName={selectedRowData}
        module="drivers"
        action={action}
      />
      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
    </div>
  );
}
